import { orderTypes } from "../types";
import { ensureRoomsJoined } from "../socketClient";
import { showSnackBar } from "../action/snackActions";

const QUANTITY_DEBOUNCE_MS = 300;
const DISCOUNT_DEBOUNCE_MS = 500;
// No ack within this time means the connection is silently broken (common on
// mobile data). We reconnect, which resends every unacked op in order.
const ACK_TIMEOUT_MS = 15000;
const MAX_ATTEMPTS = 5;
const MAX_OUTBOX_SIZE = 200;

let mutationCounter = 0;
const createMutationId = (userId) =>
  `${userId}-${Date.now().toString(36)}-${(mutationCounter++).toString(36)}`;

const getId = (item) => (item?.itemId || item?._id || item?.id)?.toString();

const getRefId = (action) =>
  action.payload?.refId || action.payload?.order?.refId;

const getQuantityKey = ({ refId, itemId, index } = {}) =>
  `qty_${refId}_${itemId}_${index ?? "last"}`;

const isQuantityFor = (entry, { refId, itemId }) =>
  entry.action.type === orderTypes.CHANGE_ITEM_QUANTITY &&
  entry.action.payload?.refId === refId &&
  entry.action.payload?.itemId?.toString() === itemId?.toString();

/**
 * Every socket action becomes an "entry" that stays tracked from the moment it
 * is dispatched until the server acks it. While an order has unacked entries
 * the reducer keeps the optimistic local copy and only stashes server
 * snapshots; once everything is acked it applies the latest snapshot. This is
 * what prevents quantities from jumping back while tapping fast.
 *
 * Each entry carries a mutationId so the server can ignore resends of an op it
 * already applied (e.g. ack lost on a flaky network).
 */
const socketMiddleware = (socket) => (store) => {
  const outbox = []; // entries waiting for a connection, in dispatch order
  const inflight = []; // entries emitted and waiting for an ack, in send order
  const debounced = new Map(); // key -> { timer, entry }
  const pendingCounts = new Map(); // refId -> number of unacked entries
  let generation = 0; // bumps on disconnect so stale acks are ignored

  const getUser = () => store.getState().user || {};

  const findOrder = (refId) =>
    store.getState().order?.activeOrders?.find((o) => o.refId === refId);

  const joinUserRooms = () => {
    const { id, branchId } = getUser();
    ensureRoomsJoined([id, branchId]);
  };

  const track = (refId, delta) => {
    if (!refId) return;
    const prev = pendingCounts.get(refId) || 0;
    const next = Math.max(0, prev + delta);
    if (next === 0) pendingCounts.delete(refId);
    else pendingCounts.set(refId, next);
    if ((prev === 0) !== (next === 0)) {
      store.dispatch({
        type: orderTypes.SET_ORDER_SYNC_PENDING,
        payload: { refId, pending: next > 0 },
      });
    }
  };

  const createEntry = (action) => {
    const entry = {
      action,
      refId: getRefId(action),
      mutationId: createMutationId(getUser().id),
      payload: null,
      attempts: 0,
      settled: false,
    };
    track(entry.refId, 1);
    return entry;
  };

  const settle = (entry) => {
    if (entry.settled) return;
    entry.settled = true;
    const index = inflight.indexOf(entry);
    if (index >= 0) inflight.splice(index, 1);
    track(entry.refId, -1);
  };

  // Built on first send so queued/debounced actions carry the latest state.
  // Kept for resends so the server sees the same op (same mutationId).
  const buildPayload = (entry) => {
    const { action } = entry;
    const { branchId, restaurantId, id } = getUser();
    const order = findOrder(entry.refId);

    if (action.type === orderTypes.CHANGE_ITEM_QUANTITY) {
      // Absolute quantity of the item in the KOT the user edited, so a single
      // collapsed emit carries every tap.
      const itemId = action.payload?.itemId?.toString();
      const kots = order?.KOTS || [];
      const kotIndex =
        typeof action.payload?.index === "number" && kots[action.payload.index]
          ? action.payload.index
          : kots.length - 1;
      const kotItem = kots[kotIndex]?.orderItems?.find(
        (it) => getId(it) === itemId,
      );
      return {
        branchId,
        restaurantId,
        userId: id,
        ...action.payload,
        targetQuantity: kotItem?.quantity,
        mutationId: entry.mutationId,
        clientTimestamp: Date.now(),
      };
    }

    return {
      branchId,
      restaurantId,
      userId: id,
      clientTimestamp: Date.now(),
      ...action.payload,
      mutationId: entry.mutationId,
    };
  };

  const giveUp = (entry) => {
    settle(entry);
    store.dispatch(
      showSnackBar("Some changes could not be synced. Please check the order.", "error"),
    );
  };

  const emitEntry = (entry) => {
    if (entry.attempts >= MAX_ATTEMPTS) {
      giveUp(entry);
      return;
    }
    joinUserRooms();
    if (!entry.payload) entry.payload = buildPayload(entry);
    entry.attempts += 1;
    const sentGeneration = generation;
    entry.generation = sentGeneration;
    inflight.push(entry);

    socket
      .timeout(ACK_TIMEOUT_MS)
      .emit(entry.action.type, entry.payload, (err) => {
        // Requeued by a disconnect in the meantime; the resend will settle it.
        if (entry.settled || entry.generation !== sentGeneration) return;
        if (err) {
          if (socket.connected) {
            socket.disconnect();
            socket.connect();
          }
          return;
        }
        settle(entry);
      });
  };

  const removeFromOutbox = (predicate) => {
    for (let i = outbox.length - 1; i >= 0; i--) {
      if (predicate(outbox[i])) settle(outbox.splice(i, 1)[0]);
    }
  };

  const enqueue = (entry) => {
    if (entry.action.type === orderTypes.CHANGE_ITEM_QUANTITY) {
      // Quantity is absolute, so only the newest entry per item/KOT matters.
      const key = getQuantityKey(entry.action.payload);
      removeFromOutbox(
        (queued) =>
          queued.action.type === orderTypes.CHANGE_ITEM_QUANTITY &&
          getQuantityKey(queued.action.payload) === key,
      );
    }
    if (outbox.length >= MAX_OUTBOX_SIZE) {
      giveUp(outbox.shift());
    }
    outbox.push(entry);
  };

  const send = (entry) => {
    if (!getUser().id) {
      settle(entry);
      return;
    }
    if (socket.connected) emitEntry(entry);
    else enqueue(entry);
  };

  const sendDebounced = (key, action, delay, mergePayload = false) => {
    const pending = debounced.get(key);
    let entry;
    if (pending) {
      clearTimeout(pending.timer);
      entry = pending.entry;
      entry.action = mergePayload
        ? { ...action, payload: { ...entry.action.payload, ...action.payload } }
        : action;
    } else {
      entry = createEntry(action);
    }

    const timer = setTimeout(() => {
      debounced.delete(key);
      send(entry);
    }, delay);

    debounced.set(key, { timer, entry });
  };

  const cancelDebounced = (predicate) => {
    debounced.forEach((pending, key) => {
      if (predicate(pending.entry)) {
        clearTimeout(pending.timer);
        debounced.delete(key);
        settle(pending.entry);
      }
    });
  };

  // Keep server-side ordering: pending edits on an order must reach the server
  // before e.g. a KOT or confirmation for that same order.
  const flushDebouncedFor = (refId) => {
    if (!refId) return;
    debounced.forEach((pending, key) => {
      if (pending.entry.refId === refId) {
        clearTimeout(pending.timer);
        debounced.delete(key);
        send(pending.entry);
      }
    });
  };

  socket.on("disconnect", () => {
    generation += 1;
    // Anything not acked may or may not have reached the server. Resend it
    // first, in original order; the server skips mutationIds it already applied.
    if (inflight.length) {
      outbox.unshift(...inflight.splice(0, inflight.length));
    }
  });

  // One listener for the lifetime of the store.
  socket.on("connect", () => {
    if (!getUser().id) return;
    joinUserRooms();
    outbox.splice(0, outbox.length).forEach(emitEntry);
  });

  return (next) => (action) => {
    // 1. Synchronously update Redux state first (zero latency for the UI)
    const result = next(action);

    if (!action.isSocket) return result;

    switch (action.type) {
      case orderTypes.CHANGE_ITEM_QUANTITY:
        // Collapse rapid +/- taps into one emit
        sendDebounced(getQuantityKey(action.payload), action, QUANTITY_DEBOUNCE_MS);
        break;

      case orderTypes.SET_DISCOUNT:
        // Discount/customer fields can fire per keystroke or per item change;
        // merge them so no field is lost while collapsing the emits.
        sendDebounced(
          `discount_${action.payload?.refId}`,
          action,
          DISCOUNT_DEBOUNCE_MS,
          true,
        );
        break;

      case orderTypes.REMOVE_ITEM: {
        // A pending quantity change for a removed item would re-create it.
        cancelDebounced((entry) => isQuantityFor(entry, action.payload || {}));
        removeFromOutbox((entry) => isQuantityFor(entry, action.payload || {}));
        flushDebouncedFor(action.payload?.refId);
        send(createEntry(action));
        break;
      }

      default: {
        flushDebouncedFor(getRefId(action));
        send(createEntry(action));
      }
    }

    return result;
  };
};

export default socketMiddleware;
